import { Box, Typography, useTheme } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DBRequest, useConfig } from '../../../api/api';
import TypeSkeleton from '../../../components/form/form-fields/TypeSkeleton';
import { tokens } from '../../../global/theme/tokens';
import WorkTimeReportBanner from './WorkTimeReportBanner';
import WorkTimeReportCard from './WorkTimeReportCard';
import {
  filterEntries,
  previousPeriod,
  sortEntries,
} from './workTimeReportUtils';

const WorkTimeReport = ({
  startDate,
  endDate,
  search = '',
  sortKey = 'customer',
  exportRequestId,
  onExportReady,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const config = useConfig();

  const [data, setData] = useState(null);
  const [previousData, setPreviousData] = useState(null);

  useEffect(() => {
    if (!config) return;
    setData(null);
    setPreviousData(null);

    DBRequest({
      config,
      path: `reports/work-time?startDate=${startDate}&endDate=${endDate}`,
      onResponse: (res) => setData(res?.data ?? []),
    });

    const prev = previousPeriod(startDate, endDate);
    DBRequest({
      config,
      path: `reports/work-time?startDate=${prev.startDate}&endDate=${prev.endDate}`,
      onResponse: (res) => setPreviousData(res?.data ?? []),
    });
  }, [config, startDate, endDate]);

  const visibleEntries = useMemo(() => {
    if (!data) return null;
    const filtered = filterEntries(data, search);
    return sortEntries(filtered, sortKey);
  }, [data, search, sortKey]);

  const previousByUserId = useMemo(() => {
    if (!previousData) return null;
    const map = new Map();
    previousData.forEach((e) => map.set(e.user.id, e));
    return map;
  }, [previousData]);

  // Forward "current visible" data to parent when CSV export is requested
  useEffect(() => {
    if (!exportRequestId || !visibleEntries) return;
    onExportReady?.(visibleEntries);
  }, [exportRequestId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!data) {
    return <TypeSkeleton n={8} />;
  }

  return (
    <Box className="flex flex-col gap-2 overflow-y-auto">
      <WorkTimeReportBanner entries={data} previousEntries={previousData} />

      {visibleEntries.length === 0 ? (
        <Box
          className="rounded-lg backdrop-blur-lg p-8 text-center"
          sx={{ bgcolor: colors.glass }}
        >
          <Typography sx={{ opacity: 0.7 }}>
            {t('No employees with data in this period')}
          </Typography>
        </Box>
      ) : (
        <Box className="flex flex-col gap-2">
          {visibleEntries.map((entry) => (
            <WorkTimeReportCard
              key={entry.user.id}
              userOverview={entry}
              previousOverview={
                previousByUserId
                  ? previousByUserId.get(entry.user.id)
                  : undefined
              }
              startDate={startDate}
              endDate={endDate}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default WorkTimeReport;
